/**
 * Lightweight analytics tracking utility.
 * Uses sendBeacon when available so events don't block navigation;
 * falls back to fetch with keepalive otherwise.
 * Fails silently — tracking should never break the user experience.
 *
 * Also honours a per-device "exclude from stats" flag: when
 * `localStorage.tbc_exclude_from_stats === "1"`, we tag every event with
 * `X-TBC-Exclude-From-Stats: 1` so the admin dashboard can filter this
 * device's visits out of the site-wide traffic totals.
 */
import axios from "axios";
import { API } from "@/App";

const SESSION_KEY = "tbc_session_id";
const EXCLUDE_KEY = "tbc_exclude_from_stats";

function getSessionId() {
  try {
    let id = localStorage.getItem(SESSION_KEY);
    if (!id) {
      id = `s_${Math.random().toString(36).slice(2, 12)}${Date.now().toString(36)}`;
      localStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
}

export function isExcludedFromStats() {
  try {
    return localStorage.getItem(EXCLUDE_KEY) === "1";
  } catch {
    return false;
  }
}

export function setExcludeFromStats(on) {
  try {
    if (on) {
      localStorage.setItem(EXCLUDE_KEY, "1");
    } else {
      localStorage.removeItem(EXCLUDE_KEY);
    }
    return true;
  } catch {
    return false;
  }
}

export function trackEvent(businessId, eventType, extra = {}) {
  if (!businessId || !eventType) return;

  const payload = {
    business_id: businessId,
    event_type: eventType,
    session_id: getSessionId(),
    ...extra,
  };

  const url = `${API}/analytics/track`;
  const excluded = isExcludedFromStats();

  // Prefer sendBeacon (truly non-blocking, survives page unload)
  // Note: sendBeacon cannot set custom headers, so we route through fetch
  // when the exclude flag is on. This is only a few devices ever, so no
  // meaningful perf loss.
  try {
    if (!excluded && typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      const blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
      const ok = navigator.sendBeacon(url, blob);
      if (ok) return;
    }
  } catch (e) {
    console.warn("[analytics] beacon dispatch failed", e);
  }

  const headers = excluded ? { "X-TBC-Exclude-From-Stats": "1" } : {};
  axios.post(url, payload, { withCredentials: false, headers }).catch(() => {});
}
